// Automatic garment codes, built from a per-organisation format, e.g. "{brand}{number}-{category}-{speciality}"
// gives ARC019-CV-FR: the brand's code prefix, the next number, the category's code and the main fabric's speciality.
// Other organisations (for example fashion brands) can choose their own format in Settings.

export interface GarmentCodeFormat { automatic: boolean; pattern: string; width: number }
export const DEFAULT_GARMENT_CODE: GarmentCodeFormat = { automatic: true, pattern: "{brand}{number}-{category}-{speciality}", width: 3 };
export const CODE_TOKENS: Array<[string, string]> = [
  ["{brand}", "The brand's code prefix (Settings → Database Lists → Brand), e.g. ARC"],
  ["{number}", "The next number, one higher than the highest garment number so far, e.g. 019"],
  ["{category}", "The category's code (Settings → Database Lists → Category), e.g. CV"],
  ["{speciality}", "The main fabric's speciality, e.g. FR or IFR-EARC"],
];

export function normalizeGarmentCode(v: any): GarmentCodeFormat {
  const pattern = typeof v?.pattern === "string" && v.pattern.includes("{number}") ? v.pattern.trim().slice(0, 80) : DEFAULT_GARMENT_CODE.pattern;
  const width = Math.min(6, Math.max(1, Math.round(Number(v?.width) || DEFAULT_GARMENT_CODE.width)));
  return { automatic: v?.automatic !== false, pattern, width };
}

const escRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** A regular expression that reads the parts back out of an existing code in this format. */
export function codeRegex(pattern: string): RegExp {
  let re = "";
  for (const part of pattern.split(/(\{brand\}|\{number\}|\{category\}|\{speciality\})/)) {
    if (part === "{brand}") re += "(?<brand>[A-Za-z]*?)";
    else if (part === "{number}") re += "(?<number>\\d+)";
    else if (part === "{category}") re += "(?<category>[A-Za-z0-9]*?)";
    else if (part === "{speciality}") re += "(?<speciality>.*?)";
    else re += escRe(part);
  }
  return new RegExp(`^${re}$`, "i");
}
export function parseCode(pattern: string, code: string): { brand?: string; number?: number; category?: string; speciality?: string } | null {
  const m = codeRegex(pattern).exec(code.trim());
  if (!m?.groups) return null;
  return { brand: m.groups.brand, number: m.groups.number ? Number(m.groups.number) : undefined, category: m.groups.category, speciality: m.groups.speciality };
}

/** One more than the highest number in any existing garment code of this format. */
export function nextGarmentNumber(pattern: string, codes: string[]): number {
  return codes.reduce((max, c) => Math.max(max, parseCode(pattern, c)?.number ?? 0), 0) + 1;
}

/**
 * Brand prefixes and category codes already used in existing garment codes (most common wins),
 * so organisations that haven't filled in the new list fields still get the right codes.
 */
export function inferCodeParts(pattern: string, garments: Array<{ code: string; brand?: string; category?: string }>): { brand: Record<string, string>; category: Record<string, string> } {
  const tally = { brand: new Map<string, Map<string, number>>(), category: new Map<string, Map<string, number>>() };
  for (const g of garments) {
    const p = parseCode(pattern, g.code);
    if (!p) continue;
    for (const k of ["brand", "category"] as const) {
      const name = g[k], part = p[k];
      if (!name || !part) continue;
      const m = tally[k].get(name) || new Map<string, number>();
      m.set(part.toUpperCase(), (m.get(part.toUpperCase()) || 0) + 1);
      tally[k].set(name, m);
    }
  }
  const best = (m: Map<string, Map<string, number>>) => Object.fromEntries([...m].map(([name, counts]) => [name, [...counts].sort((x, y) => y[1] - x[1])[0][0]]));
  return { brand: best(tally.brand), category: best(tally.category) };
}

/** Build the code. Parts that are empty are left out, with their separators tidied. */
export function buildGarmentCode(f: GarmentCodeFormat, parts: { brand: string; category: string; speciality: string }, number: number): string {
  const out = f.pattern
    .replace("{brand}", parts.brand.toUpperCase())
    .replace("{number}", String(number).padStart(f.width, "0"))
    .replace("{category}", parts.category.toUpperCase())
    .replace("{speciality}", parts.speciality.toUpperCase().replace(/\s+/g, "-"));
  return out.replace(/-{2,}/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
}

/** Which parts the format needs but are missing (shown to the person before saving). */
export function missingCodeParts(f: GarmentCodeFormat, parts: { brand: string; category: string; speciality: string }): string[] {
  const out: string[] = [];
  if (f.pattern.includes("{brand}") && !parts.brand) out.push("brand");
  if (f.pattern.includes("{category}") && !parts.category) out.push("category");
  if (f.pattern.includes("{speciality}") && !parts.speciality) out.push("speciality");
  return out;
}
